import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {TableColumn, TableSettings} from "../../../tableSettings";
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../common/AbstractListView";
import constants from '../../../constants';
import {Search} from "../../../filtering";
import {NavigationType, openNewPage} from '../../../util/navigationHelpers';
import {
  DateRenderer,DateTimeRenderer,FloatRenderer,NullableCheckboxIconRenderer,TextAlignLeftRenderer,TextRenderer,
} from '../../../uiControls/SmartTableRenderers';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import ApplicationContext from '../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../common/JobListViewContext';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import {oceanExportLoadPlanList} from "../tableSettings";
import {JobNames} from "../../../framework.core/JobNames";
import {OeLoadPlanListItem} from "../../../fms.data/models/OeLoadPlanListItem";
import PrimaryButton from "../../../ui/PrimaryButton";
import ListViewShortcutMenu from '../../common/ListViewShortcutMenu';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../ui/ActionButton';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../ui/menu/Menu';
import * as XCol from '../../../framework.core/XExportColumn';
import { FmsSecTask } from '../../../fms.core/constants';
import { findSecurityTask } from '../../../util';
import { EditIconButton, EditIconButton2, ViewIconButton, ViewIconButton2 } from '../../../ui/IconButton';

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();

  const selectedElement = jobListContext.selectedElement as OeLoadPlanListItem;

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl(`/oceanImport/loadPlan/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    openNewPage(history, `/oceanImport/loadPlan/add`, NavigationType.newPage);
  }

  async function remove() {
    if (!selectedElement) {
      return;
    }

    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    , false, 0, true, 'warning');

    if (doDelete) {
      await appContext.api.deleteOceanImportMblById(selectedElement.id);
      jobListContext.reloadData();
    }
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/oceanExport/loadPlan/edit/${id}`;
    const viewUrl = `/oceanExport/loadPlan/view/${id}`;

    // if (jobListContext.hasOpenedScreenStartingWithUrlWithoutMe('/oceanExport/loadPlan/edit', editUrl)) {
    //   await showGlobalOkDialog(<p>Edit operation is already in progress</p>);
    //   return;
    // }

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/oceanExport/loadPlan/edit/${id}`;
    const viewUrl = `/oceanExport/loadPlan/view/${id}`;
    const listSec = findSecurityTask(appContext, FmsSecTask.OELoad);
    const urls = [viewUrl];
    if (listSec?.grantEdit) urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  async function copy() {
    if (!selectedElement) {
      return;
    }

    if (jobListContext.hasOpenedScreenStartingWithUrl('/oceanExport/loadPlan/add')) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    const id = selectedElement.id;
    openNewPage(history, `/oceanExport/loadPlan/add?copyId=${id}`, NavigationType.newPage);
  }

  return {create, remove, edit, view, copy};
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { edit, view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  // const selectedElement = jobListContext.selectedElement as OeLoadPlanListItem;
   const appContext = useContext(ApplicationContext);
  // const [showGlobalOkDialog] = useGlobalOkDialog();
  const listSec = findSecurityTask(appContext, FmsSecTask.OELoad);

  const items = [
    { label: 'View', shortcut: 'F9', separator: false },
    { label: 'Edit', shortcut: 'F3', separator: false, hidden: !listSec?.grantEdit },
    { label: 'Search', shortcut: 'F5', separator: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="oeLoadPlan-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    index,
    hidden: i?.hidden ?? false,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        view();
        break;
      case 1:
        edit();
        break;
      case 2:
        jobListContext.triggerTableSearch('F5');
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ListCommands() {
  const jobListContext = useContext(JobListViewContext);

  const selectedElement = jobListContext.selectedElement as OeLoadPlanListItem;

  const { edit, view,} = useSelectedOperations();

  // function hasOpenedViewOrEditFor(id:number) {
  //   return jobListContext.hasOpenedScreenStartingWithUrl(`/oceanImport/loadPlan/edit/${id}`) || jobListContext.hasOpenedScreenStartingWithUrl(`/oceanImport/loadPlan/view/${id}`);
  // }

  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.OELoad);

  const hasSelectedRow = !!selectedElement;
  //const canBeDeleted = hasSelectedRow && !hasOpenedViewOrEditFor(selectedElement.id) && listSec?.grantDel;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;

  return (
    <div className="ifms-common-no-vertical-separator" style={{ marginRight:'84px'}}>
      {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ? <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"} /> : <></>} */}
      <div className='ifms-common-vertical-gap-8'>
        <EditIconButton2 name='oeLoadPlan-list-edit-button' disabled={!canBeEdit} onClick={edit} useText />
        <ViewIconButton2 name='oeLoadPlan-list-view-button' disabled={!hasSelectedRow} onClick={view} useText />
      </div>
      {/* <PrimaryButton size="regular" disabled={!canBeDeleted} onClick={remove}>Delete</PrimaryButton> */}
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.OELoad);

  const {edit, view} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F2") {
    } else if (unprocessedFkey === "F3") {
      if(listSec?.grantEdit)
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F2', 'F3', 'F9', 'F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }
  return (
    <></>
  );
}

/**
 * Concrete implementation for Load Plan List screen
 *
 * @class LoadPlanListView
 * @extends {AbstractListView}
 */
class LoadPlanListView extends AbstractListView {

  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.OELoad,
      'OeLoadPlanListItem',
      OeLoadPlanListItem.calculateSearchParameters(search, OeLoadPlanListItem.MapOeSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: OeLoadPlanListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.OELoad, 'OeLoadPlanListItem',
      OeLoadPlanListItem.calculateSearchParameters(search, OeLoadPlanListItem.MapOeSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  OeLoadPlanListItem.formatData(apiResult.entities);
  }
  

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {
    return OeLoadPlanListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if(x.dataIndex === "status") {
        colRenderer.Cell = () => <span></span>;
        colRenderer.Header = () => <span></span>;
      }
      if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.type === XCol.XCellType.Double) colRenderer.Cell = FloatRenderer;

      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'id',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings =  oceanExportLoadPlanList;
    const cols = OeLoadPlanListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: OeLoadPlanListItem) {
    return row && row.id ? `/oceanExport/loadPlan/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Load Plan List';
  };

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  hasMemo(): boolean {
    return false;
  }
}

export default LoadPlanListView;
